import { useState, useEffect } from "react"
import TextField from "component-lib/Base/components/TextField/TextField"
import { withLocalization } from "hoc-lib/localization"
import { isInternalEmail } from "utils-lib/validation"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import noop from "utils-lib/noop"

export const strings = {
  en_CA: {
    EMAIL: `Email`,
    VALIDATING: `Validating`,
    INVALID_EMAIL: `Invalid email`,
  },
  fr_CA: {
    EMAIL: `Email`,
    VALIDATING: `Valider`,
    INVALID_EMAIL: `Email invalide`,
  },
}

const EmailInput = ({
  getEmailCleanse,
  label,
  strings,
  userAuthenticationRequestStatuses,
  value,
  className = ``,
  fullWidth = false,
  customErrorMessage = ``,
  onBlur = noop,
  onChange = noop,
  onStatusChange = noop,
  style = {},
  required = false,
  variant = ``,
  disabled = false,
  allowInternal = false,
  error = false,
  doNotcallEmailCleanse = false,
  formattedId = "",
}) => {
  const [emailCleanseStatus, setEmailCleanseStatus] = useState(``)

  const isAutofillChangeEvent = (event) => {
    const inputType = event?.nativeEvent?.inputType

    if (inputType === `insertReplacementText`) {
      return true
    }

    if (typeof event?.target?.matches === `function`) {
      try {
        return event.target.matches(`:-webkit-autofill`)
      } catch {
        return false
      }
    }

    return false
  }

  const { LDFFReleaseCheckAvailabilityButtonStaysDisabled } = useFeatureFlags([
    "LDFFReleaseCheckAvailabilityButtonStaysDisabled",
  ])

  useEffect(() => {
    if (
      userAuthenticationRequestStatuses?.emailStatusState?.status ===
      `fulfilled`
    ) {
      const { statusCode } = userAuthenticationRequestStatuses.emailStatusState

      if (
        statusCode === `unknown` ||
        statusCode === `valid` ||
        statusCode === `corrected`
      ) {
        setEmailCleanseStatus(`validated`)
        onStatusChange(`validated`)
      } else {
        setEmailCleanseStatus(`error`)
        onStatusChange(`error`)
      }
    } else if (
      userAuthenticationRequestStatuses?.emailStatusState?.status === `failed`
    ) {
      setEmailCleanseStatus(`error`)
      onStatusChange(`error`)
    }
  }, [userAuthenticationRequestStatuses?.emailStatusState?.status])

  let emailCleanseErrorMessage

  if (emailCleanseStatus === `error`) {
    emailCleanseErrorMessage = strings.INVALID_EMAIL
  }

  if (
    userAuthenticationRequestStatuses?.emailStatusState?.status === `pending`
  ) {
    emailCleanseErrorMessage = strings.VALIDATING
  }

  return (
    <>
      {LDFFReleaseCheckAvailabilityButtonStaysDisabled ? (
        <TextField
          variant={variant || "outlined"}
          type="email"
          fullWidth={fullWidth}
          className={className}
          data-testid="EmailInput"
          style={style}
          label={label || strings.EMAIL}
          inputProps={{ "aria-label": label || strings.EMAIL }}
          onChange={(event) => {
            const { value } = event.target

            setEmailCleanseStatus(``)
            onStatusChange(``)

            onChange(value)
            if (value && isAutofillChangeEvent(event)) {
              getEmailCleanse(value)
            }
          }}
          onKeyPress={(e) => {
            if (e.key === ` `) {
              e.preventDefault()
            }
          }}
          value={value}
          id={formattedId}
          onBlur={
            allowInternal && !doNotcallEmailCleanse
              ? () => {
                  if (value) {
                    getEmailCleanse(value)
                  }

                  onBlur()
                }
              : () => {
                  if (value && !doNotcallEmailCleanse) {
                    if (!isInternalEmail(value)) {
                      getEmailCleanse(value)
                    } else {
                      setEmailCleanseStatus(`error`)
                      onStatusChange(`error`)
                    }
                  }

                  onBlur()
                }
          }
          error={
            error || Boolean(customErrorMessage || emailCleanseErrorMessage)
          }
          helperText={customErrorMessage || emailCleanseErrorMessage}
          required={required}
          disabled={disabled}
        />
      ) : (
        <TextField
          variant={variant || "outlined"}
          type="email"
          fullWidth={fullWidth}
          className={className}
          data-testid="EmailInput"
          style={style}
          label={label || strings.EMAIL}
          inputProps={{ "aria-label": label || strings.EMAIL }}
          onChange={({ target: { value } }) => {
            setEmailCleanseStatus(``)
            onStatusChange(``)

            onChange(value)
          }}
          onKeyPress={(e) => {
            if (e.key === ` `) {
              e.preventDefault()
            }
          }}
          value={value}
          id={formattedId}
          onBlur={
            allowInternal && !doNotcallEmailCleanse
              ? () => {
                  if (value) {
                    getEmailCleanse(value)
                  }
                  onBlur()
                }
              : () => {
                  if (value && !doNotcallEmailCleanse) {
                    if (!isInternalEmail(value)) {
                      getEmailCleanse(value)
                    } else {
                      setEmailCleanseStatus(`error`)
                      onStatusChange(`error`)
                    }
                  }
                  onBlur()
                }
          }
          error={
            error || Boolean(customErrorMessage || emailCleanseErrorMessage)
          }
          helperText={customErrorMessage || emailCleanseErrorMessage}
          required={required}
          disabled={disabled}
        />
      )}
    </>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// EmailInput.propTypes = {
//   getEmailCleanse: PropTypes.func.isRequired,
//   label: PropTypes.string.isRequired,
//   strings: PropTypes.object.isRequired,
//   userAuthenticationRequestStatuses: PropTypes.object.isRequired,
//   value: PropTypes.string.isRequired,
//   className: PropTypes.string,
//   fullWidth: PropTypes.bool,
//   customErrorMessage: PropTypes.string,
//   onBlur: PropTypes.func,
//   onChange: PropTypes.func,
//   onStatusChange: PropTypes.func,
//   style: PropTypes.object,
//   required: PropTypes.bool,
//   variant: PropTypes.string,
//   disabled: PropTypes.bool,
//   allowInternal: PropTypes.bool,
//   error: PropTypes.bool,
//   doNotcallEmailCleanse: PropTypes.bool,
//   formattedId: PropTypes.string,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// EmailInput.defaultProps = {
//   className: ``,
//   fullWidth: false,
//   customErrorMessage: ``,
//   onBlur: noop,
//   onChange: noop,
//   onStatusChange: noop,
//   style: {},
//   required: false,
//   variant: ``,
//   disabled: false,
//   allowInternal: false,
//   error: false,
//   doNotcallEmailCleanse: false,
//   formattedId: "",
// }

export default withLocalization(strings)(EmailInput)
